import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { WidgetsService } from '@components/widgets/widgets.service';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { VehicleStore } from '@modules/core/store/vehicle.store';
import { AppStateService } from '@modules/core/services/app-state.service';
import { TranslateService } from '@modules/core/services/translate.service';
import { TranslateModule } from '@ngx-translate/core';
import { Vehicle } from '@modules/shared/models/vehicle';
import { BaseWidgetDirective } from '@components/widgets/widgetsbase.directive';
import { WidgetDropdownDirective } from '@components/widgets/widget-dropdown.directive';
import { SpinnerComponent } from '@modules/shared/components/spinner/spinner.component';
import { WarningMessageType } from '@modules/shared/models/vehicle/vehicle-warning';
import { DeviationStyle, deviationStyle } from '@components/widgets/utils/departure-deviation-colors';
import { MatTooltipModule } from '@angular/material/tooltip';

enum LateDepFilterStop {
    all = 'all',
    timingPoint = 'timingPoint',
    startStop = 'startStop'
}

const FILTER_STATE_KEY = 'oms.widget.filter.late-departures';

@Component({
    selector: 'app-late-departures-widget',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerComponent, TranslateModule, BaseWidgetDirective, DragDropModule, MatTooltipModule, WidgetDropdownDirective],
    templateUrl: './late-departures-widget.component.html',
    styleUrls: ['./late-departures-widget.component.scss'],
})
export class LateDeparturesWidgetComponent {
    public readonly widgetState = inject(WidgetsService);
    private readonly _ts = inject(TranslateService);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _appState = inject(AppStateService);
    public readonly widgetTitle = signal<string>('');
    public readonly widgetId = signal<string>('widget-late-departures');
    public readonly fillRemaining = signal<boolean>(false);
    public readonly fixedSize = signal<boolean>(false);
    public readonly defaultFraction = signal<number>(0.25);
    public readonly sortCol = signal<'inv' | 'route' | 'stop' | 'dev' | 'next'>('dev');
    public readonly sortDir = signal<'asc' | 'desc'>('asc');
    public readonly FilterStop = LateDepFilterStop;
    public readonly stopFilter = signal<LateDepFilterStop>(LateDepFilterStop.all);
    public readonly minDeviationMinutes = signal<number>(0);
    public readonly filterActive = computed(() =>
        this.stopFilter() !== LateDepFilterStop.all || this.minDeviationMinutes() > 0
    );

    public readonly lateDepartures = computed<Vehicle[]>(
        () => {
            const vehicles = this._vehicles();
            if (vehicles == undefined) return undefined;

            const col = this.sortCol();
            const dir = this.sortDir();
            const stop = this.stopFilter();
            const minMins = this.minDeviationMinutes();

            const filtered = vehicles.filter(v => {
                const devSecs = v.departureDeviation?.deviation.getTotalSeconds();
                if (devSecs == undefined || devSecs >= 0) return false;
                if (minMins > 0 && Math.abs(devSecs) < minMins * 60) return false;
                if (stop === LateDepFilterStop.startStop && v.departureDeviation?.sequenceNumber !== 1) return false;
                if (stop === LateDepFilterStop.timingPoint && !v.departureDeviation?.isTimingPoint) return false;
                return true;
            });

            return filtered.sort((a, b) => {
                let cmp = 0;
                switch (col) {
                    case 'inv':   cmp = a.inventoryNumberWithoutPrefix.localeCompare(b.inventoryNumberWithoutPrefix); break;
                    case 'route': cmp = (a.routeNumber ?? '').localeCompare(b.routeNumber ?? ''); break;
                    case 'stop':  cmp = (a.departureDeviation?.stopName ?? '').localeCompare(b.departureDeviation?.stopName ?? ''); break;
                    case 'dev':   cmp = a.departureDeviation.deviation.getTotalSeconds() - b.departureDeviation.deviation.getTotalSeconds(); break;
                    case 'next': {
                        const aS = this._nextTripDelaySecs(a) ?? 0;
                        const bS = this._nextTripDelaySecs(b) ?? 0;
                        cmp = aS - bS;
                        break;
                    }
                }
                return dir === 'asc' ? cmp : -cmp;
            });
        }
    );

    private readonly _vehicles = toSignal(
        this._vehicleStore.getVehiclesState(),
        { initialValue: undefined }
    );

    constructor() {
        this.widgetTitle.set(this._ts.instant(_translate('Late departures')));
        const saved = this._loadState();
        this.stopFilter.set(saved.stopFilter);
        this.minDeviationMinutes.set(saved.minDeviationMinutes);
    }

    public setSort(col: 'inv' | 'route' | 'stop' | 'dev' | 'next'): void {
        if (this.sortCol() === col) {
            this.sortDir.update(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            this.sortCol.set(col);
            this.sortDir.set('asc');
        }
    }

    public setStopFilter(value: LateDepFilterStop): void {
        this.stopFilter.set(value);
        this._saveState();
    }

    public increaseMinDeviation(): void {
        this.minDeviationMinutes.update(v => v + 1);
        this._saveState();
    }

    public decreaseMinDeviation(): void {
        this.minDeviationMinutes.update(v => Math.max(0, v - 1));
        this._saveState();
    }

    public setMinDeviation(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        const value = Math.max(0, Math.floor(Number(raw)) || 0);
        this.minDeviationMinutes.set(value);
        this._saveState();
    }

    public onDeviationKeyDown(event: KeyboardEvent): void {
        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!allowed.includes(event.key) && !/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    public resetFilter(): void {
        this.stopFilter.set(LateDepFilterStop.all);
        this.minDeviationMinutes.set(0);
        this._saveState();
    }

    public departureDeviationStyle(v: Vehicle): DeviationStyle {
        const secs = v.departureDeviation?.deviation?.getTotalSeconds();
        if (secs == undefined) return undefined;
        return deviationStyle(secs, this._appState.stateSnapshot.punctualityConfig);
    }

    public nextTripDelayStyle(v: Vehicle): DeviationStyle {
        const secs = this._nextTripDelaySecs(v);
        if (secs == undefined) return undefined;
        return deviationStyle(-secs, this._appState.stateSnapshot.punctualityConfig);
    }

    public nextTripDelay(v: Vehicle): string {
        const secs = this._nextTripDelaySecs(v);
        if (secs == undefined) return undefined;
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        const mm = ('00' + m).slice(-2);
        const ss = ('00' + s).slice(-2);
        return `-${mm}:${ss}`;
    }

    private _nextTripDelaySecs(v: Vehicle): number {
        const w = v.warnings?.find(w => w.messageType === WarningMessageType.VehicleDelay);
        if (!w) return undefined;
        const secs: number = w.metadata?.['riskOfDelayInSeconds'];
        return secs ?? undefined;
    }

    private _loadState(): { stopFilter: LateDepFilterStop; minDeviationMinutes: number } {
        try {
            const raw = localStorage.getItem(FILTER_STATE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                return {
                    stopFilter: parsed.stopFilter ?? LateDepFilterStop.all,
                    minDeviationMinutes: parsed.minDeviationMinutes ?? 0
                };
            }
        } catch { /* ignore */ }
        return { stopFilter: LateDepFilterStop.all, minDeviationMinutes: 0 };
    }

    private _saveState(): void {
        localStorage.setItem(FILTER_STATE_KEY, JSON.stringify({
            stopFilter: this.stopFilter(),
            minDeviationMinutes: this.minDeviationMinutes()
        }));
    }
}
